import {
  Badge,
  Button,
  Card,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconTemplate } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { hiringFlowsApi } from "../lib/api";
import {
  BUILTIN_FLOW_PRESETS,
  SCRATCH_PRESET,
  SCRATCH_PRESET_ID,
  type FlowPreset,
} from "../lib/flowPresets";
import type { StageInput } from "../types/api";

export type FlowSelectionSource = "preset" | "company" | "scratch";

export interface FlowSelection {
  id: string;
  source: FlowSelectionSource;
  stages: StageInput[];
  name: string;
}

interface FlowTemplatePickerProps {
  companyId?: string;
  selectedId: string | null;
  selectedSource: FlowSelectionSource | null;
  hasEdits?: boolean;
  onSelect: (selection: FlowSelection) => void;
}

function StageChips({ stages }: { stages: StageInput[] }) {
  if (stages.length === 0) {
    return (
      <Text size="xs" c="dimmed">
        No stages — add your own
      </Text>
    );
  }
  return (
    <Group gap="var(--axio-unit-4)">
      {stages.map((s, i) => (
        <Badge key={i} size="xs" variant="outline" color="gray">
          {s.name}
        </Badge>
      ))}
    </Group>
  );
}

function TemplateCard({
  preset,
  selected,
  onClick,
}: {
  preset: FlowPreset;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Card
      withBorder
      padding="md"

      style={{
        cursor: "pointer",
        borderColor: selected ? "var(--mantine-color-violet-5)" : undefined,
        background: selected ? "var(--mantine-color-violet-0)" : undefined,
      }}
      onClick={onClick}
    >
      <Stack gap="xs">
        <Group justify="space-between">
          <Text fw="var(--axio-font-weight-semibold)" size="sm">
            {preset.name}
          </Text>
          <Badge size="xs" variant="outline" color="gray">
            {preset.stages.length} stage{preset.stages.length !== 1 ? "s" : ""}
          </Badge>
        </Group>
        <Text size="xs" c="dimmed" lineClamp={2}>
          {preset.description}
        </Text>
        <StageChips stages={preset.stages} />
      </Stack>
    </Card>
  );
}

export function FlowTemplatePicker({
  companyId,
  selectedId,
  selectedSource,
  hasEdits = false,
  onSelect,
}: FlowTemplatePickerProps) {
  const [pending, setPending] = useState<FlowSelection | null>(null);

  const templatesQuery = useQuery({
    queryKey: ["hiring-templates", companyId],
    queryFn: () => hiringFlowsApi.listTemplates(companyId!),
    enabled: !!companyId,
  });

  function handleSelect(selection: FlowSelection) {
    if (hasEdits && selectedId && selectedId !== selection.id) {
      setPending(selection);
      return;
    }
    onSelect(selection);
  }

  function confirmReplace() {
    if (pending) {
      onSelect(pending);
      setPending(null);
    }
  }

  const companyTemplates = templatesQuery.data ?? [];

  return (
    <Stack gap="md">
      <div>
        <Title order={5}>Choose a starting template</Title>
        <Text size="sm" c="dimmed">
          Pick a preset or saved company template, then customize stages below.
        </Text>
      </div>

      <div>
        <Text size="xs" fw="var(--axio-font-weight-semibold)" c="dimmed" mb="xs">
          Recommended presets
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          {BUILTIN_FLOW_PRESETS.map((preset) => (
            <TemplateCard
              key={preset.id}
              preset={preset}
              selected={selectedSource === "preset" && selectedId === preset.id}
              onClick={() =>
                handleSelect({
                  id: preset.id,
                  source: "preset",
                  name: preset.name,
                  stages: preset.stages.map((s) => ({ ...s })),
                })
              }
            />
          ))}
        </SimpleGrid>
      </div>

      {companyId && (
        <div>
          <Text size="xs" fw="var(--axio-font-weight-semibold)" c="dimmed" mb="xs">
            Your company templates
          </Text>
          {companyTemplates.length === 0 ? (
            <Text size="sm" c="dimmed">
              No saved templates yet. Save one from the stage editor below or from the Templates page.
            </Text>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              {companyTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  preset={{
                    id: template.id,
                    name: template.name,
                    description: template.description ?? "Company template",
                    segmentHints: [],
                    stages: template.stages.map((s) => ({
                      name: s.name,
                      stage_type: s.stage_type,
                      is_mandatory: s.is_mandatory,
                      description: s.description,
                    })),
                  }}
                  selected={selectedSource === "company" && selectedId === template.id}
                  onClick={() =>
                    handleSelect({
                      id: template.id,
                      source: "company",
                      name: template.name,
                      stages: template.stages.map((s) => ({
                        name: s.name,
                        stage_type: s.stage_type,
                        is_mandatory: s.is_mandatory,
                        description: s.description,
                      })),
                    })
                  }
                />
              ))}
            </SimpleGrid>
          )}
        </div>
      )}

      <div>
        <Text size="xs" fw="var(--axio-font-weight-semibold)" c="dimmed" mb="xs">
          Custom
        </Text>
        <Card
          withBorder
          padding="md"

          style={{
            cursor: "pointer",
            borderColor:
              selectedSource === "scratch" && selectedId === SCRATCH_PRESET_ID
                ? "var(--mantine-color-violet-5)"
                : undefined,
            background:
              selectedSource === "scratch" && selectedId === SCRATCH_PRESET_ID
                ? "var(--mantine-color-violet-0)"
                : undefined,
          }}
          onClick={() =>
            handleSelect({
              id: SCRATCH_PRESET_ID,
              source: "scratch",
              name: SCRATCH_PRESET.name,
              stages: [],
            })
          }
        >
          <Group gap="sm">
            <IconTemplate size="var(--axio-unit-20)" color="var(--mantine-color-violet-6)" />
            <div>
              <Text fw="var(--axio-font-weight-semibold)" size="sm">
                {SCRATCH_PRESET.name}
              </Text>
              <Text size="xs" c="dimmed">
                {SCRATCH_PRESET.description}
              </Text>
            </div>
          </Group>
        </Card>
      </div>

      <Modal
        opened={!!pending}
        onClose={() => setPending(null)}
        title="Replace current stages?"
        centered
      >
        <Stack>
          <Text size="sm">
            Switching templates will replace your current stage configuration. Continue?
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button color="violet" onClick={confirmReplace}>
              Replace stages
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

export function FlowPreviewStepper({ stages }: { stages: StageInput[] }) {
  if (stages.length === 0) return null;
  return (
    <Group gap="xs" wrap="wrap">
      {stages.map((stage, i) => (
        <Group key={i} gap="var(--axio-unit-4)">
          <Badge size="sm" variant="filled" color="violet" circle>
            {i + 1}
          </Badge>
          <Text size="sm">{stage.name}</Text>
          {i < stages.length - 1 && (
            <Text size="sm" c="dimmed">
              →
            </Text>
          )}
        </Group>
      ))}
    </Group>
  );
}
